import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { IMaskInput } from 'react-imask';
import cn from 'classnames';
import useAxios from 'axios-hooks';

import { Button, ProfileTabs, SelectRaw } from '../../../components';

var PASSWORD_REGEX =
	/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,20}$/;

const Profile = ({ user, showNotification }) => {
	const userRaw = user.toJSON();
	const {
		name_first,
		name_middle,
		name_last,
		credentials,
		provider_code,
		provider_practice,
		usePracticeDefaultPrice,
		providerPrice,
		notification_number,
		appointment_only_ind,
	} = userRaw;

	const [isSaving, setIsSaving] = useState(false);
	const [usePracticePrice, setUsePracticePrice] = useState(
		usePracticeDefaultPrice
	);
	const [providerFee, setProviderFee] = useState(providerPrice);
	const [notificationNumber, setNotificationNumber] =
		useState(notification_number);
	const [appointmentsOnly, setAppointmentsOnly] =
		useState(appointment_only_ind);
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');

	const noMerchantCode = isEmpty(provider_practice.cc_merchant_id);
	const [notificationNumberHasError, setNotificationNumberHasError] =
		useState(false);
	const [badPasswordLength, setBadPasswordLength] = useState(false);
	const [badPassword, setBadPassword] = useState(false);
	const [passwordsDoNotMatch, setPasswordsDoNotMatch] = useState(false);

	const [, /*{ data, loading, error } */ getProfilePicContainer] = useAxios(
		{
			method: 'GET',
			url: `/provider_profile_images/${userRaw.id}`,
		},
		{ manual: true }
	);
	const [, /* { data, loading, error } */ createProfilePicContainer] = useAxios(
		{
			method: 'PUT',
			url: '/provider_profile_images',
		},
		{ manual: true }
	);

	useEffect(() => {
		// build profile pic container
		const buildContainer = async () => {
			try {
				getProfilePicContainer();
			} catch (e) {
				createProfilePicContainer({
					data: {
						name: `${userRaw.id}`,
					},
				});
			}
		};
		buildContainer();
	}, []);

	useEffect(() => {
		if (appointmentsOnly !== user.get('appointment_only_ind')) {
			user.set('appointment_only_ind', appointmentsOnly);
			setIsSaving(true);
			user.save().then((updatedUser) => {
				showNotification({ message: 'Profile Updated', status: 'success' });
				setAppointmentsOnly(updatedUser.appointment_only_ind);
				setIsSaving(false);
			});
		}
	}, [appointmentsOnly]);

	const handleSetAppointmentsOnly = (value) => {
		const isByAppointmentOnly = value === 'true';
		setAppointmentsOnly(isByAppointmentOnly);
	};

	const handleSave = (event) => {
		event.preventDefault();

		let isInvalid = false;
		setNotificationNumberHasError(false);
		setBadPasswordLength(false);
		setPasswordsDoNotMatch(false);
		setBadPassword(false);

		if (notificationNumber.length < 10) {
			isInvalid = true;
			setNotificationNumberHasError(true);
		}

		if (!isEmpty(password)) {
			if (password.length < 7 || password.length > 20) {
				isInvalid |= true;
				setBadPasswordLength(true);
			}
			if (!PASSWORD_REGEX.test(password)) {
				isInvalid |= true;
				setBadPassword(true);
			}
			if (confirmPassword !== password) {
				isInvalid |= true;
				setPasswordsDoNotMatch(true);
			}
		}

		if (!isInvalid) {
			user.set('notification_number', notificationNumber);
			user.set('usePracticeDefaultPrice', usePracticePrice);
			if (!usePracticePrice) {
				user.set('providerPrice', providerFee);
			}
			if (!isEmpty(password)) {
				user.set('password', password);
			}
			// save user
			user.save().then((/* updatedUser */) => {
				showNotification({ message: 'Profile Updated', status: 'success' });
				setIsSaving(false);
			});
		}
	};

	return (
		<div className="content-wrapper profile">
			<ProfileTabs activeTab="profile" />
			<div className="row mt-xl">
				<form className="container form-horizontal">
					<fieldset>
						<div className="form-group">
							<label className="col-md-4 control-label">First Name</label>
							<div className="col-md-8">
								<p className="form-control-static">{name_first}</p>
							</div>
						</div>
						<div className="form-group">
							<label className="col-md-4 control-label">Middle Name</label>
							<div className="col-md-8">
								<p className="form-control-static">{name_middle}</p>
							</div>
						</div>
						<div className="form-group">
							<label className="col-md-4 control-label">Last Name</label>
							<div className="col-md-8">
								<p className="form-control-static">{name_last}</p>
							</div>
						</div>
						<div className="form-group">
							<label className="col-md-4 control-label">Credentials</label>
							<div className="col-md-8">
								<p className="form-control-static">{credentials}</p>
								<span className="help-block m-b-none">
									Please submit a credentialing change request for name and/or
									credential change.
								</span>
							</div>
						</div>
					</fieldset>
					<fieldset>
						<div className="form-group">
							<label className="col-md-4 control-label">Practice</label>
							<div className="col-md-8">
								<p className="form-control-static">
									{provider_practice.practice_name}
								</p>
							</div>
						</div>
						<div className="form-group">
							<label className="col-md-4 control-label">Practice Code</label>
							<div className="col-md-8">
								<p className="form-control-static">
									{provider_practice.practice_code}
								</p>
							</div>
						</div>
					</fieldset>
					<fieldset>
						<div
							className={cn('form-group', {
								'has-error': notificationNumberHasError,
							})}
						>
							<label
								htmlFor="notification_number"
								className="col-md-4 control-label"
							>
								Notification Number
							</label>
							<div className="col-md-4">
								<IMaskInput
									mask="(000) 000-0000"
									className="form-control"
									id="notification_number"
									value={notificationNumber}
									onAccept={(value) => setNotificationNumber(value)}
									unmask
								/>
								<span className="help-block m-b-none">
									Number must be capable of receiving SMS/Text messages
								</span>
							</div>
						</div>
						<div className="form-group">
							<label htmlFor="provider_code" className="col-md-4 control-label">
								Provider Code
							</label>
							<div className="col-md-8">
								<p className="form-control-static">{provider_code}</p>
							</div>
						</div>
					</fieldset>
					<fieldset>
						<div className="form-group">
							<label className="col-md-4 control-label">Visit Fee</label>
							<div className="col-md-8">
								<div className="radio c-radio">
									<label>
										<input
											type="radio"
											checked={usePracticePrice}
											disabled={noMerchantCode}
											onChange={() => setUsePracticePrice(true)}
											name="usePracticeDefaultPrice"
										/>
										<span className="fa fa-circle" />
										Use Practice Fee
									</label>
								</div>
								<br />
							</div>
						</div>
						<div className="form-group">
							<div className="col-md-offset-4 col-md-2">
								<div className="radio c-radio">
									<label>
										<input
											type="radio"
											checked={!usePracticePrice}
											disabled={noMerchantCode}
											name="usePracticeDefaultPrice"
											onChange={() => setUsePracticePrice(false)}
										/>
										<span className="fa fa-circle" />
										Use My Fee
									</label>
								</div>
							</div>
							<div className="col-md-3">
								<div className="input-group">
									<span className="input-group-addon">$</span>
									<input
										type="text"
										className="form-control"
										value={providerFee}
										onChange={({ target }) => setProviderFee(target.value)}
										disabled={usePracticePrice || noMerchantCode}
									/>
								</div>
							</div>
							{noMerchantCode && (
								<div className="col-md-offset-4 col-md-5">
									<span className="help-block">
										<strong>No merchant code found for your practice!</strong>
										<br />
										Please contact your Care Convene admin to help set this up.
										You will not be able to charge patients until this is
										complete.
									</span>
								</div>
							)}
						</div>
					</fieldset>
					<fieldset>
						<label className="col-md-4 control-label">
							Visit Types Allowed
						</label>
						<div className="col-md-4 pb">
							<SelectRaw
								selectedValue={appointmentsOnly}
								onChange={({ target }) =>
									handleSetAppointmentsOnly(target.value)
								}
								options={[
									{ value: 'true', label: 'Appointment Only' },
									{ value: 'false', label: 'Appointment and On-Demand' },
								]}
							/>
						</div>
					</fieldset>
					<div
						className={cn('form-group', {
							'has-error':
								passwordsDoNotMatch || badPasswordLength || badPassword,
						})}
					>
						<label htmlFor="password" className="col-md-4 control-label">
							Change Password
						</label>
						<div className="col-md-4">
							<input
								type="password"
								className="form-control"
								id="password"
								name="password"
								placeholder="New Password"
								onChange={({ target }) => setPassword(target.value)}
							/>
						</div>
						<div className="col-md-4">
							<input
								type="password"
								className="form-control"
								id="password1"
								name="password1"
								placeholder="Confirm Password"
								onChange={({ target }) => setConfirmPassword(target.value)}
							/>
						</div>
						<div className="col-md-8 col-md-offset-4">
							<span className="help-block m-b-none">
								<ul>
									{badPasswordLength && (
										<li>
											Password must be at least 8 and no more than 20 characters
											long
										</li>
									)}
									{badPassword && (
										<li>
											Password must contain 1 lower case letter, 1 upper case
											letter, 1 number, 1 special character (!@#$%^&amp;)
										</li>
									)}
									{passwordsDoNotMatch && <li>Passwords do not match</li>}
								</ul>
							</span>
						</div>
					</div>
					<div className="form-group">
						<div className="col-md-offset-4 col-md-8">
							<Button
								primary
								className="save"
								title="Save"
								disabled={isSaving}
								onClick={handleSave}
							/>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

Profile.propTypes = {
	user: PropTypes.object.isRequired,
	showNotification: PropTypes.func.isRequired,
};

export default Profile;
